// TODO move this to a component pkg
import { ID } from "@nova/constants"
import classNames from "classnames"
import React, {
    forwardRef,
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react"
import { ConfirmControls } from "@nova/component-confirm-controls"
import { useTranslation } from "@nova/use-translation"
import { Coordinate } from "@nova/protobuf"
import * as Icons from "@nova/component-icons"

const latitudeRegex = /^-?(?:|(?:(?:0|[1-8]?\d)(?:\.\d*)?|90(?:\.0*)?|\.\d*))$/
const longitudeRegex = /^(?:-?|-?(?:(?:0|[1-9]\d?|1[0-7]\d|180)(?:\.\d*)?|\.\d*))$/

type RenamableCoordinateProps = {
    Tag: "h1" | "h2" | "h3" | "h4" | "h5" | "small"

    coordinate: Coordinate
    id: ID
    isRenaming: boolean

    /**
     * If background is bg-gray-700, the hover state for the confirm/cancel
     * controls will blend in. Set to `true` to brighten the controls hover
     * state in this special circumstance
     * @default false
     */
    backgroundIsDark?: boolean

    /**
     * Whether the background of the text should highlight on hover.
     * @default false
     */
    shouldHighlightOnHover?: boolean

    /**
     * Whether it should fill the available width.
     * @default true
     */
    isFullWidth?: boolean

    /**
     * An important height value should be passed in depending on the tag.
     * @example "!h-6"
     */
    className?: string

    onClick?: (e: MouseEvent) => void
    onConfirmRenaming?: (id: ID, coordinate: Coordinate) => void
    onCancelRenaming?: () => void
}

const RenamableCoordinate = forwardRef(
    (
        {
            Tag,
            coordinate,
            isRenaming,
            id,
            backgroundIsDark = false,
            shouldHighlightOnHover = true,
            className,
            isFullWidth = true,
            onClick,
            onConfirmRenaming,
            onCancelRenaming,
        }: RenamableCoordinateProps,
        ref
    ) => {
        const { t } = useTranslation()

        const inputRef = useRef<HTMLInputElement>(null)
        const [renamedTitles, setRenamedTitles] = useState([
            coordinate.latitude.toString(),
            coordinate.longitude.toString(),
        ])
        const [isHovering, setIsHovering] = useState(false)

        useImperativeHandle(ref, () => ({
            focus: () => {
                inputRef.current?.focus()
            },
        }))

        const handleKeyDown = useCallback(
            (e: KeyboardEvent) => {
                e.stopPropagation()

                if (e.key === "Enter" && isRenaming) {
                    const sanitizedTitles = [renamedTitles[0].trimEnd(), renamedTitles[1].trimEnd()]
                    if (sanitizedTitles.some((e) => e.length === 0)) return
                    onConfirmRenaming?.(id, {
                        latitude: Number(sanitizedTitles[0]),
                        longitude: Number(sanitizedTitles[1]),
                    })
                    setRenamedTitles(sanitizedTitles)
                } else if (e.key === "Escape" && isRenaming) {
                    onCancelRenaming?.()
                    setRenamedTitles([
                        coordinate.latitude.toString(),
                        coordinate.longitude.toString(),
                    ])
                }
            },
            [renamedTitles, coordinate, onConfirmRenaming, isRenaming, onCancelRenaming, id]
        )

        const [shouldShowCopySuccess, setShouldShowCopySuccess] = useState(false)
        const copySuccessTimerRef = useRef<NodeJS.Timeout>(undefined)
        const copyCoordinate = async (e: MouseEvent) => {
            e.stopPropagation()

            try {
                await navigator.clipboard.writeText(
                    `${coordinate.latitude.toFixed(6)}, ${coordinate.longitude.toFixed(6)}`
                )
                setShouldShowCopySuccess(true)
                copySuccessTimerRef.current = setTimeout(
                    () => setShouldShowCopySuccess(false),
                    1000
                )
            } catch {
                console.error("Failed to copy coordinate")
            }
        }

        useEffect(() => {
            return () => {
                if (copySuccessTimerRef.current) clearTimeout(copySuccessTimerRef.current)
            }
        }, [])

        return (
            <div
                className={classNames(
                    "flex-none flex items-center min-w-0 border-b gap-1",
                    className,
                    {
                        "border-white-300 text-gray-0 -mt-1": isRenaming,
                        "border-transparent -mt-[0.3125rem] text-gray-300 hover:text-gray-0":
                            !isRenaming,
                        "w-full": isFullWidth,
                        "-mb-[0.3125rem]": shouldHighlightOnHover,
                    }
                )}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={onClick}
            >
                <div
                    className={classNames("px-1.5 py-1 rounded-md -ml-1.5", {
                        "bg-gray-500": isHovering && !isRenaming && shouldHighlightOnHover,
                    })}
                >
                    <Tag
                        className={classNames(
                            "select-none text-ellipsis overflow-hidden whitespace-nowrap",
                            {
                                "flex-grow": isFullWidth,
                            }
                        )}
                    >
                        {!isRenaming ? (
                            t(
                                `${coordinate.latitude.toPrecision(8)}º, ${coordinate.longitude.toPrecision(8)}º`
                            )
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    className={classNames(
                                        "p-0 bg-transparent border-0 !shadow-none min-w-0 w-full flex-grow leading-[inherit]",
                                        "focus:outline-none focus:border-transparent focus:ring-0"
                                    )}
                                    style={{ fontSize: "inherit" }}
                                    ref={inputRef}
                                    type="text"
                                    value={renamedTitles[0]}
                                    onChange={(e) => {
                                        const sanitizedTitle = e.target.value.trimStart()
                                        if (!latitudeRegex.test(sanitizedTitle)) return
                                        setRenamedTitles((prev) => [sanitizedTitle, prev[1]])
                                    }}
                                    onKeyDown={handleKeyDown}
                                    placeholder={"Latitude"}
                                />
                                <input
                                    className={classNames(
                                        "p-0 bg-transparent border-0 !shadow-none min-w-0 w-full flex-grow leading-[inherit]",
                                        "focus:outline-none focus:border-transparent focus:ring-0"
                                    )}
                                    style={{ fontSize: "inherit" }}
                                    ref={inputRef}
                                    type="text"
                                    value={renamedTitles[1]}
                                    onChange={(e) => {
                                        const sanitizedTitle = e.target.value.trimStart()
                                        if (!longitudeRegex.test(sanitizedTitle)) return
                                        setRenamedTitles((prev) => [prev[0], sanitizedTitle])
                                    }}
                                    onKeyDown={handleKeyDown}
                                    placeholder={"Longitude"}
                                />
                            </div>
                        )}
                    </Tag>
                </div>

                {/* Copy Button */}
                {(isHovering || shouldShowCopySuccess) && !isRenaming && (
                    <Icons.SmallIcon
                        Icon={shouldShowCopySuccess ? Icons.Checkmark : Icons.Copy}
                        className={classNames("cursor-pointer", {
                            "text-gray-300 hover:text-gray-0": !shouldShowCopySuccess,
                            "text-gray-0": shouldShowCopySuccess,
                        })}
                        onClick={(e) => copyCoordinate(e)}
                    />
                )}

                {isRenaming && (
                    <ConfirmControls
                        isValid={renamedTitles.every(
                            (title) => title.replace(".", "").replace("-", "").length > 0
                        )}
                        iconClassName={classNames({
                            "hover:bg-gray-500": backgroundIsDark,
                            "hover:bg-gray-700": !backgroundIsDark,
                        })}
                        onConfirm={() => {
                            const sanitizedTitles = [
                                renamedTitles[0].trimEnd(),
                                renamedTitles[1].trimEnd(),
                            ]
                            if (sanitizedTitles.some((e) => e.length === 0)) return
                            onConfirmRenaming?.(id, {
                                latitude: Number(sanitizedTitles[0]),
                                longitude: Number(sanitizedTitles[1]),
                            })
                            setRenamedTitles(sanitizedTitles)
                        }}
                        onCancel={() => {
                            onCancelRenaming?.()
                            setRenamedTitles([
                                coordinate.latitude.toString(),
                                coordinate.longitude.toString(),
                            ])
                        }}
                    />
                )}
            </div>
        )
    }
)
RenamableCoordinate.displayName = "RenamableCoordinate"
export { RenamableCoordinate }
